import { useCallback } from 'react'
import { defaultChildAge, initialRoom } from './PersonSelector.data'
import { ChildAge, PersonSelectorProps } from './PersonSelector.types'
import { useGetSummaryLabel } from './PersonSelector.hooks'

interface PersonSelectorControllerProps extends Pick<
  PersonSelectorProps,
  'rooms' | 'onChange' | 'roomSelector'
> {}

export const usePersonSelectorController = ({
  rooms,
  onChange,
  roomSelector,
}: PersonSelectorControllerProps) => {
  const onAdultsChange = useCallback(
    (roomIndex: number, value: number) => {
      const updatedRooms = [...rooms]
      updatedRooms[roomIndex].adults = value
      onChange(updatedRooms)
    },
    [onChange, rooms],
  )

  const onChildrenChange = useCallback(
    (roomIndex: number, value: number) => {
      const updated = [...rooms]
      updated[roomIndex].children = value

      updated[roomIndex].childrenAges = updated[roomIndex].childrenAges.slice(0, value)
      updated[roomIndex].dropdownOpen = updated[roomIndex].dropdownOpen.slice(0, value)

      while (updated[roomIndex].childrenAges.length < value) {
        updated[roomIndex].childrenAges.push({ ...defaultChildAge })
        updated[roomIndex].dropdownOpen.push(false)
      }
      onChange(updated)
    },
    [onChange, rooms],
  )

  const onChildrenAgeChange = useCallback(
    (roomIndex: number, childIndex: number, option: ChildAge) => {
      const updated = [...rooms]
      updated[roomIndex].childrenAges[childIndex] = option
      onChange(updated)
    },
    [onChange, rooms],
  )

  const toggleChildAgeDropdown = useCallback(
    (roomIndex: number, childIndex: number, isOpen: boolean) => {
      const updated = [...rooms]
      updated[roomIndex].dropdownOpen[childIndex] = isOpen
      onChange(updated)
    },
    [onChange, rooms],
  )

  const addRoom = useCallback(() => {
    onChange([...rooms, initialRoom()])
  }, [onChange, rooms])

  const removeRoom = useCallback(
    (roomIndex: number) => {
      const updated = rooms.filter((_, i) => i !== roomIndex)
      onChange(updated)
    },
    [onChange, rooms],
  )

  const summaryLabel = useGetSummaryLabel(rooms, roomSelector)

  return {
    summaryLabel,
    onAdultsChange,
    onChildrenChange,
    onChildrenAgeChange,
    toggleChildAgeDropdown,
    addRoom,
    removeRoom,
  }
}
